export function gridDividers(element, args = {}, uniqueId = false) {
    if (!element || !uniqueId) return;

    const {divider, breakpoints: bpConfig = {}} = args;
    if (!divider) return;

    const container = element.querySelector(':scope > .grid-container');
    if (!container) return;

    // ✅ SINGLE SOURCE OF TRUTH
    const CARD_SELECTOR = '.wpbs-loop-card';

    const cards = container.querySelectorAll(CARD_SELECTOR);
    const total = cards.length;
    if (total === 0) return;

    const selector = '.' + uniqueId;

    // --------------------------------------------------
    // Base columns (mobile)
    // --------------------------------------------------
    const colBase = parseInt(args?.props?.columns ?? '1', 10) || 1;
    const lastRowBase = total % colBase || colBase;

    const themeBreakpoints = window.WPBS?.settings?.breakpoints || {};

    // --------------------------------------------------
    // Normalize breakpoints (small → large)
    // --------------------------------------------------
    const bpDefs = Object.entries(bpConfig)
        .map(([key, entry = {}]) => {
            const cols = parseInt(entry?.props?.columns ?? 0, 10) || 0;
            const raw = themeBreakpoints[key];
            const sizeNumber =
                typeof raw === 'object' && raw?.size
                    ? parseInt(raw.size, 10)
                    : 0;

            return {key, sizeNumber, cols};
        })
        .filter(bp => bp.cols > 0 && bp.sizeNumber > 0)
        .sort((a, b) => a.sizeNumber - b.sizeNumber);

    // --------------------------------------------------
    // Divider rule generator
    // --------------------------------------------------
    const buildRules = (cols, lastRow) => [
        `${selector} .grid-container > ${CARD_SELECTOR}:nth-of-type(${cols}n+1):after {
            content: none !important;
        }`,

        `${selector} .grid-container > ${CARD_SELECTOR}:nth-of-type(n+${cols + 1}):after {
            height: calc(100% + (var(--grid-row-gap, var(--grid-col-gap)) / 2));
            top: calc(0px - (var(--grid-row-gap, var(--grid-col-gap, 0px)) / 2));
        }`,

        `${selector} .grid-container:has(> ${CARD_SELECTOR}:nth-of-type(${cols + 1}))
        > ${CARD_SELECTOR}:before {
            content: "";
        }`,

        `${selector} .grid-container:has(> ${CARD_SELECTOR}:nth-of-type(${cols + 1}))
        > ${CARD_SELECTOR}:nth-of-type(-n+${cols + 1}):after {
            height: calc(100% + (var(--grid-row-gap, var(--grid-col-gap)) / 2));
            top: 0;
        }`,

        `${selector} .grid-container:has(> ${CARD_SELECTOR}:nth-of-type(${cols + 1}))
        > ${CARD_SELECTOR}:nth-of-type(n+${cols + 2}):after {
            height: calc(100% + var(--grid-row-gap, var(--grid-col-gap, 0px)));
            top: calc(0px - (var(--grid-row-gap, var(--grid-col-gap, 0px)) / 2));
        }`,

        `${selector} .grid-container > ${CARD_SELECTOR}:nth-of-type(${cols}n):before {
            width: calc(100% + (var(--grid-col-gap) / 2));
        }`,

        `${selector} .grid-container > ${CARD_SELECTOR}:nth-of-type(${cols}n+1):before {
            width: ${cols > 1
            ? 'calc(100% + (var(--grid-col-gap) / 2))'
            : '100%'};
            left: 0;
        }`,

        `${selector} .grid-container:has(> ${CARD_SELECTOR}:nth-of-type(${cols + 1}))
        > ${CARD_SELECTOR}:nth-last-of-type(-n+${lastRow}):after {
            height: calc(100% + (var(--grid-row-gap, var(--grid-col-gap)) / 2)) !important;
            top: calc(0px - (var(--grid-row-gap, var(--grid-col-gap, 0px)) / 2));
        }`,

        `${selector} .grid-container > ${CARD_SELECTOR}:nth-last-of-type(-n+${lastRow}):before {
            content: none !important;
        }`,
    ];

    // --------------------------------------------------
    // Build CSS
    // --------------------------------------------------
    let styleCss = '';

    // ✅ BASE RANGE: below first breakpoint
    if (bpDefs.length) {
        const max = bpDefs[0].sizeNumber - 1;
        styleCss += `
@media screen and (max-width: ${max}px) {
${buildRules(colBase, lastRowBase).join('\n')}
}
`;
    } else {
        styleCss += buildRules(colBase, lastRowBase).join('\n');
    }

    // ✅ RANGED BREAKPOINTS
    for (let i = 0; i < bpDefs.length; i++) {
        const bp = bpDefs[i];
        const next = bpDefs[i + 1] ?? null;

        const cols = bp.cols;
        const lastRow = total % cols || cols;

        const min = bp.sizeNumber;
        const max = next ? next.sizeNumber - 1 : null;

        const media = max
            ? `screen and (min-width: ${min}px) and (max-width: ${max}px)`
            : `screen and (min-width: ${min}px)`;

        styleCss += `
@media ${media} {
${buildRules(cols, lastRow).join('\n')}
}
`;
    }

    // --------------------------------------------------
    // Divider icon vars
    // --------------------------------------------------
    if (divider?.icon) {
        const icon = divider.icon;
        styleCss += `
${selector} {
    --divider-icon: ${icon.name ? `"${icon.name}"` : '""'};
    --divider-icon-size: ${icon.size ? icon.size + 'px' : '1em'};
    --divider-icon-color: ${icon.color || 'currentColor'};
    --divider-size: ${divider?.border?.width};
}
`;
    }

    // --------------------------------------------------
    // Inject style
    // --------------------------------------------------
    const styleClass = `${uniqueId}-divider-styles`;
    document.querySelectorAll('.' + styleClass).forEach(t => t.remove());

    const styleTag = document.createElement('style');
    styleTag.className = styleClass;
    styleTag.textContent = styleCss;
    document.head.appendChild(styleTag);

    element.classList.add('--divider');
}
